import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { Bootstrap } from '@us/shared';
import { api, ApiRequestError } from './api';
import { applyAccentColors } from './theme';

interface AuthCtx {
  boot: Bootstrap | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** 서버 부트스트랩을 다시 받아 상태 갱신(어드민 편집 후 호출). */
  refresh: () => Promise<void>;
  /** 부분 갱신(낙관적). 액센트도 재적용. */
  patchBoot: (next: Bootstrap) => void;
}

const AuthContext = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [boot, setBoot] = useState<Bootstrap | null>(null);
  const [loading, setLoading] = useState(true);

  const applyBoot = useCallback((next: Bootstrap | null) => {
    setBoot(next);
    if (next) applyAccentColors(next.users);
  }, []);

  useEffect(() => {
    api
      .get<Bootstrap>('/api/auth/me')
      .then((b) => applyBoot(b))
      .catch((e) => {
        if (!(e instanceof ApiRequestError && e.status === 401)) console.error(e);
        applyBoot(null);
      })
      .finally(() => setLoading(false));
  }, [applyBoot]);

  const login = useCallback(
    async (username: string, password: string) => {
      const b = await api.post<Bootstrap>('/api/auth/login', { username, password });
      applyBoot(b);
    },
    [applyBoot],
  );

  const logout = useCallback(async () => {
    await api.post('/api/auth/logout');
    applyBoot(null);
  }, [applyBoot]);

  const refresh = useCallback(async () => {
    const b = await api.get<Bootstrap>('/api/auth/me');
    applyBoot(b);
  }, [applyBoot]);

  return (
    <AuthContext.Provider
      value={{ boot, loading, login, logout, refresh, patchBoot: applyBoot }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthCtx {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
